import * as React from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import range from 'lodash/range';

import { ReduxState } from 'ducks';
import { Product } from 'models/product';

import { ReviewCard } from './ReviewCard';
import { ReviewSearchForm } from './ReviewSearchForm';
import { SearchFormValues } from './review.utils';
import styles from './ProductReviewsPane.module.css';
import { ProductReviewsSummary } from './ProductReviewsSummary';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { fetchPageOfReviews, fetchReviews } from 'ducks/universal/reviews';

const defaultPageSize = 30;

interface Props {
  product: Product | null;
  onSearchFormValuesChange: (searchFormValues: SearchFormValues) => void;
  searchFormValues: SearchFormValues;
  onClose: () => void;
}

export const ProductReviewsModal = ({
  product,
  searchFormValues,
  onSearchFormValuesChange,
  onClose,
}: Props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [offset, setOffset] = React.useState(0);
  const { apiKey } = React.useContext(ApiKeyContext);

  const modalRef = React.useRef<HTMLDivElement>(null);
  const loading = useSelector((state: ReduxState) => state.universal.reviews.loading);
  const lastFetchedPage = useSelector(
    (state: ReduxState) => state.universal.reviews.lastFetchedPage
  );

  const reviews = useSelector((state: ReduxState) => state.universal.reviews.all);
  const showSeeMoreButton =
    (reviews?.length ?? 0) > 0 && (lastFetchedPage?.length ?? 0) > defaultPageSize;

  React.useEffect(() => {
    const onWindowClick = (event: MouseEvent | TouchEvent) => {
      if (modalRef && !modalRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };

    window.addEventListener('mousedown', onWindowClick);

    return () => {
      window.removeEventListener('mousedown', onWindowClick);
    };
  }, []);

  React.useEffect(() => {
    if (product?.id) {
      setOffset(0);
      const months = range(searchFormValues.monthStart, searchFormValues.monthEnd + 1);

      dispatch(
        fetchReviews(product.booking_widget_api_key || apiKey, i18n.language, {
          productIds: [product?.id],
          ratings: searchFormValues.ratings,
          languagesFilter: searchFormValues.language
            ? ([searchFormValues.language] as any)
            : undefined,
          attributions: searchFormValues.attributions,
          months,
          pageSize: defaultPageSize + 1,
        })
      );
    }
  }, [apiKey, i18n.language, product?.id, searchFormValues, product?.booking_widget_api_key]);

  const fetchNextPage = React.useCallback(() => {
    const newOffset = offset + defaultPageSize;
    if (product?.id) {
      const months = range(searchFormValues.monthStart, searchFormValues.monthEnd + 1);

      (async () => {
        await dispatch(
          fetchPageOfReviews(apiKey, i18n.language, {
            productIds: [product?.id],
            ratings: searchFormValues.ratings,
            languagesFilter: searchFormValues.language
              ? ([searchFormValues.language] as any)
              : undefined,
            attributions: searchFormValues.attributions,
            months,
            offset: newOffset,
            pageSize: defaultPageSize + 1,
          })
        );

        setOffset(newOffset);
      })();
    }
  }, [apiKey, i18n.language, product?.id, searchFormValues, offset]);

  if (!product?.review_count) {
    return null;
  }

  return (
    <div className={styles['c-review__modal']}>
      <div ref={modalRef} className={styles['c-review__modal__content']}>
        <div className={styles['c-review__modal__content__header']}>
          <p className={styles['c-review__modal__content__header__ttl']}>
            {t('All Customer Reviews')}
          </p>
          <a className={styles['c-review__modal__content__header__close']} onClick={onClose}></a>
        </div>
        <div className={styles['c-review__modal__content__body']}>
          <div className={styles['c-review__summary']}>
            <ProductReviewsSummary product={product} />
            <ReviewSearchForm
              onSearchFormValuesChange={onSearchFormValuesChange}
              searchFormValues={searchFormValues}
              initialIsExpanded={false}
            />
          </div>
          <div className={styles['c-review__list']}>
            <p className={styles['c-review__ttl']}>{t('Customer Reviews')}</p>
            <ul className={styles['c-review__list__comment']}>
              {reviews?.map((review, idx) => (
                <ReviewCard key={idx} review={review} />
              ))}
            </ul>
            {showSeeMoreButton && (
              <a
                className={clsx(styles['c-review__list__btn'], loading && styles['loading'])}
                onClick={() => fetchNextPage()}
              >
                {t('Show More Reviews')}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
